import { useMemo, useRef } from "react";

import type { ViewJob } from "../../../__generated__/types.gqlgenerated";
import type { Locale } from "../../../shared/I18n";
import { useViewjobProctorAllocation } from "../../proctor/__generated__/ViewjobProctorContext";
import { HeRnViewjobOverviewTst } from "../../proctor/__generated__/ViewjobProctorSpecifications";
import type { ViewjobQueryResult } from "../__generated__/viewjob.gqlgenerated";
import { isHiringEvent } from "../components/core/HiringEvents/utils/isHiringEvent";
import getJobKeyFromUrl from "../util/getJobKeyFromUrl";
import { normalizeViewJobUrl } from "../util/normalizeViewJobUrl";
import {
  useConditionalHiringEventsData,
  type HiringEventData,
} from "./useConditionalHiringEventsData";
import { useViewJobData } from "./useViewJobData";

export type ViewJobInitialDataResult = {
  loading: boolean;
  data: {
    viewJobData: ViewjobQueryResult["data"];
    hiringEventsData?: HiringEventData | undefined;
  };
  error: Error | undefined;
  queryStatus: "idle" | "active" | "success" | "error";
  /**
   * Settles with the primary viewjob refetch only — it resolves when that query
   * comes back and rejects when it fails. The hiring-events refetch is fired
   * alongside it but not awaited, so it affects neither outcome.
   */
  refetch: () => Promise<unknown>;
  fetchStartTimeMs: number | null;
  fetchEndTimeMs: number | null;
};

export const useViewJobInitialData = (
  url: string | null,
  locale: Locale,
): ViewJobInitialDataResult => {
  const jobKey = getJobKeyFromUrl(url);
  const normalizedUrl = normalizeViewJobUrl(url, locale);
  const fetchStartTimeMsRef = useRef<number | null>(null);
  const fetchEndTimeMsRef = useRef<number | null>(null);

  const heRnViewJobOverviewTestAllocation = useViewjobProctorAllocation(HeRnViewjobOverviewTst);

  const { data, loading, error, refetch } = useViewJobData(normalizedUrl || "", jobKey || "");
  const criticalError =
    error && !(data?.viewjob?.job && data?.viewjob?.details) ? error : undefined;

  // Track fetch start timestamp (only when query is actually loading)
  if (loading && fetchStartTimeMsRef.current === null) {
    fetchStartTimeMsRef.current = Date.now();
  }

  // Track fetch end timestamp (only after we've seen a loading state)
  if (
    !loading &&
    data &&
    fetchStartTimeMsRef.current !== null &&
    fetchEndTimeMsRef.current === null
  ) {
    fetchEndTimeMsRef.current = Date.now();
  }
  // Determine if this is a hiring event from the ViewJob model
  const shouldFetchHiringEventData =
    heRnViewJobOverviewTestAllocation?.isTest &&
    !!data?.viewjob &&
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
    isHiringEvent(data.viewjob as ViewJob);

  // Conditionally fetch hiring events data based on ViewJob response
  const hiringEventsData = useConditionalHiringEventsData(shouldFetchHiringEventData, jobKey);

  const queryStatus = useMemo(() => {
    // If ViewJob is still loading or failed, use its status
    if (loading) return "active";
    if (criticalError) return "error";
    if (!data) return "idle";

    // ViewJob succeeded - now check hiring events if needed
    if (shouldFetchHiringEventData) {
      if (!hiringEventsData.called || hiringEventsData.loading) return "active";
      if (hiringEventsData.error) return "error";
    }

    // All required data is ready
    return "success";
  }, [loading, criticalError, data, shouldFetchHiringEventData, hiringEventsData]);

  const retryRequest = () => {
    // Reset timestamps for new fetch
    fetchStartTimeMsRef.current = null;
    fetchEndTimeMsRef.current = null;

    // Also refetch hiring events data if it was previously fetched
    if (shouldFetchHiringEventData && hiringEventsData.refetch && hiringEventsData.called) {
      void hiringEventsData.refetch();
    }

    // Returned rather than voided so callers that care about failure (e.g. the
    // Match Overview hydration hook) can react to it instead of waiting out a timeout.
    return refetch();
  };

  return {
    loading: queryStatus === "active",
    data: {
      viewJobData: data,
      hiringEventsData: hiringEventsData.hiringEvent,
    },
    error: criticalError || hiringEventsData.error,
    queryStatus,
    refetch: retryRequest,
    fetchStartTimeMs: fetchStartTimeMsRef.current,
    fetchEndTimeMs: fetchEndTimeMsRef.current,
  };
};
